import React, { useEffect, useMemo, useRef } from 'react';
import t from 'react-translate';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { getCurrentCourse } from 'redux/selectors/course';
import { useSelector } from 'react-redux';
import { IHelpOptionsSettingsPayload, AdditionalResourceStatus } from 'redux/schemas/models/help-options';
import { useAppDispatch } from 'redux/store';
import { getHelpOptionsSettings, saveHelpOptionsSettings } from 'redux/actions/help-options';
import { getAdditionalResources, getHasAdditionalResourcesChanged } from 'redux/selectors/help-options';
import pusherService from 'shared/services/pusher-service';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { PopoversContainerContext } from 'shared/react-utils';
import AILearningAssistantSection from './ai-learning-assistant-section';
import HelpResourcesSection from './help-resources-section';
import { modalBodyStyles } from './styles';
import { AskNovoAiState } from '../ask-novoai/helpers';
import type { HelpOptionsSettingsProps, IHelpOptionsFormValues } from './types';

// Yup validation schema
const validationSchema = yup.object().shape({
  userCourseIds: yup.array().of(yup.number()),
  hasAiLearningAssistantEnabled: yup.boolean().nullable(),
  hasHelpResourcesEnabled: yup.boolean().nullable(),
  courseHelpTitle: yup.string().when('hasHelpResourcesEnabled', {
    is: true,
    then: yup.string().required().min(1),
    otherwise: yup.string(),
  }),
  courseHelpBody: yup.string().when('hasHelpResourcesEnabled', {
    is: true,
    then: yup.string().required().min(1),
    otherwise: yup.string(),
  }),
});


const QNA_PUSHER_EVENT = 'qna_embedding_completed';
const ADDITIONAL_RESOURCES_PUSHER_EVENT = 'additional_resources_embedded';

interface PusherChannelPayload {
  has_qna_enabled: boolean
}

const HelpOptionsSettings = (props: HelpOptionsSettingsProps) => {
  const { forwardShowModal } = props;
  const dispatch = useAppDispatch();
  const [show, setShow] = React.useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { hasAiLearningAssistant } = useSelector(getCurrentInstitution);
  const { catalogId, id } = useSelector(getCurrentCourse) as {
    catalogId: string;
    id: number;
  };

  const askNovoAi: AskNovoAiState = useSelector(
    (state) => state.app.askNovoAi,
  );
  const { helpOptions } = askNovoAi;
  const additionalResources = useSelector(getAdditionalResources);
  const hasAdditionalResourcesChanged = useSelector(getHasAdditionalResourcesChanged);

  // Track if the form has been initialized for the current modal session
  const isFormInitialized = React.useRef(false);

  const methods = useForm<IHelpOptionsFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      userCourseIds: [],
      hasAiLearningAssistantEnabled: false,
      hasHelpResourcesEnabled: false,
      courseHelpTitle: '',
      courseHelpBody: '',
    },
  });
  const watchUserCourseIds = methods.watch('userCourseIds');
  const watchHasAiLearningAssistantEnabled = methods.watch('hasAiLearningAssistantEnabled');

  React.useEffect(() => {
    // Only reset the form if it hasn't been initialized yet for this modal session
    if (helpOptions && show && !isFormInitialized.current) {
      const {
        courseAdminUsers = [],
        hasAiLearningAssistantEnabled,
        hasHelpResourcesEnabled,
        hasAiEmbeddingInProgress,
      } = helpOptions;

      methods.reset({
        userCourseIds: courseAdminUsers?.reduce(
          (acc: number[], item) => {
            if (item.id && item.isQnaFacilitator) {
              return [...acc, item.id];
            }
            return acc;
          },
          [],
        ),
        hasAiLearningAssistantEnabled: hasAiLearningAssistant && (hasAiLearningAssistantEnabled || hasAiEmbeddingInProgress),
        hasHelpResourcesEnabled,
        courseHelpTitle: hasHelpResourcesEnabled ? helpOptions.courseHelp?.title : '',
        courseHelpBody: hasHelpResourcesEnabled ? helpOptions.courseHelp?.body : '',
      });

      // Mark the form as initialized for this modal session
      isFormInitialized.current = true;
    }
  }, [hasAiLearningAssistant, helpOptions, methods, show]);


  // Listening for changes in the discussion key themes
  React.useEffect(() => {
    let pusherChannel = null;
    pusherChannel = pusherService.initializeCourseChannel(id);
    pusherChannel.bind(ADDITIONAL_RESOURCES_PUSHER_EVENT, () => {
      dispatch(getHelpOptionsSettings(catalogId));
    })
    pusherChannel.bind(QNA_PUSHER_EVENT, (data: PusherChannelPayload) => {
      if (data.has_qna_enabled) {
        dispatch(getHelpOptionsSettings(catalogId));
      }
    });

    return () => {
      if (pusherChannel) {
        pusherChannel.unbind(QNA_PUSHER_EVENT);
        pusherChannel.unbind(ADDITIONAL_RESOURCES_PUSHER_EVENT);
      }
    };
  }, [catalogId, dispatch, id]);

  React.useEffect(() => {
    forwardShowModal(() => setShow(true));
  }, [forwardShowModal]);

  const handleModalClose = () => {
    setShow(false);
    // Reset the initialization flag so the form can be re-initialized on next open
    isFormInitialized.current = false;
    // Set the additional resources to the initial state
    dispatch(getHelpOptionsSettings(catalogId));
  };

  const onSubmit = async (data: IHelpOptionsFormValues) => {
    const resourceIds = additionalResources
      .filter((resource) => resource.id && resource.status !== AdditionalResourceStatus.UPLOADING)
      .map((resource) => resource.id);

    const payload: IHelpOptionsSettingsPayload = {
      userCourseIds: data.userCourseIds,
      hasAiLearningAssistantEnabled: data.hasAiLearningAssistantEnabled,
      hasHelpResourcesEnabled: data.hasHelpResourcesEnabled,
      courseHelp: {
        title: data.courseHelpTitle,
        body: data.courseHelpBody,
      },
      additionalResourceIds: resourceIds,
    };

    await dispatch(saveHelpOptionsSettings({ catalogId, payload }));
    setShow(false);
  };

  useEffect(() => {
    // Clean up the modal state when the modal is closed
    if (!show) {
      methods.reset();
      // Reset the initialization flag when modal closes
      isFormInitialized.current = false;
    }
  }, [show, methods]);

  const disabledButton = useMemo(
    () => {
      const hasUploadingResources = additionalResources.some(
        (resource) => resource.status === AdditionalResourceStatus.UPLOADING,
      );
      if (hasUploadingResources) return true;

      // If user course IDs are selected, AI Learning Assistant must be enabled
      if (hasAiLearningAssistant && watchUserCourseIds.length > 0 && !watchHasAiLearningAssistantEnabled) return true;

      const hasFormChanged = methods.formState.isDirty || hasAdditionalResourcesChanged;
      return !hasFormChanged || !methods.formState.isValid;
    },
    [
      methods.formState.isDirty,
      methods.formState.isValid,
      watchUserCourseIds,
      hasAiLearningAssistant,
      watchHasAiLearningAssistantEnabled,
      additionalResources,
      hasAdditionalResourcesChanged,
    ],
  );

  return (
    <NvModal
      show={show}
      height='auto'
      type={ModalType.FIXED}
      onClose={handleModalClose}
      header={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.BADGE_TITLE()}
      body={(
        <div ref={modalContentRef}>
          <PopoversContainerContext.Provider value={modalContentRef.current}>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div css={modalBodyStyles}>
                  <AILearningAssistantSection
                    users={helpOptions?.courseAdminUsers}
                  />
                  <HelpResourcesSection />
                  <div className='custom-modal-footer'>
                    <button
                      type='button'
                      className='btn btn-default'
                      onClick={handleModalClose}
                      >
                      {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.CANCEL_BUTTON()}
                    </button>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      disabled={disabledButton || methods.formState.isSubmitting}
                      >
                      {methods.formState.isSubmitting ? t.FORM.SAVING() : t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.CONFIRM_BUTTON()}
                    </button>
                  </div>
                </div>
              </form>
            </FormProvider>
          </PopoversContainerContext.Provider>
        </div>
      )}
    />
  );
};

export default HelpOptionsSettings;
